// src/components/Auth/AuthGuard.tsx の拡張

import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../Common/LoadingSpinner';
import { useUser } from '../../hooks/useUser';
import IncompleteWorksModal from '../Construction/IncompleteWorksModal';
import { constructionService } from '../../services/construction';

interface AuthGuardProps {
  children: ReactNode;
}

const AuthGuard = ({ children }: AuthGuardProps) => {
  const auth = useAuth();
  const { userInfo, isLoading: userInfoLoading } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  
  // 未完了工事確認モーダル用のステート
  const [showIncompleteWorksModal, setShowIncompleteWorksModal] = useState(false);
  const [weeklyCheckCompleted, setWeeklyCheckCompleted] = useState(false);

  // 認証とナビゲーションの処理
  useEffect(() => {
    // stateエラーの場合は再認証を試みる
    if (auth.error?.message === 'No matching state found in storage') {
      auth.signinRedirect();
      return;
    }

    // 認証完了かつユーザー情報取得完了時の処理
    if (auth.isAuthenticated && !userInfoLoading && userInfo) {
      if (location.pathname === '/auth-callback') {
        navigate('/construction', { replace: true });
      }
      
      // 週の初回ログインチェックを実行（一度だけ）
      if (!weeklyCheckCompleted) {
        checkWeeklyLogin();
      }
    }
  }, [auth.error, auth.isAuthenticated, userInfoLoading, userInfo, location.pathname, weeklyCheckCompleted]);

  // 週の初回ログインチェックの実行
  const checkWeeklyLogin = async () => {
    try {
      // 既に認証済みでユーザー情報が取得できている場合のみ実行
      if (auth.isAuthenticated && userInfo) {
        const isFirstLoginOfWeek = await constructionService.checkWeeklyLogin();
        
        if (isFirstLoginOfWeek) {
          setShowIncompleteWorksModal(true);
        }
        
        // チェックが完了したことを記録
        setWeeklyCheckCompleted(true);
      }
    } catch (error) {
      console.error('週の初回ログイン確認エラー:', error);
      // エラーが発生してもチェック完了とマーク
      setWeeklyCheckCompleted(true);
    }
  };

  // ローディングとエラー時の表示制御
  if (auth.error?.message === 'No matching state found in storage') {
    return <LoadingSpinner />;
  }

  if (auth.isLoading || userInfoLoading) {
    return <LoadingSpinner />;
  }

  // 未認証時のリダイレクト
  if (!auth.isAuthenticated && location.pathname !== '/login') {
    return <Navigate to="/login" />;
  }

  return (
    <>
      {children}
      
      {/* 未完了工事確認モーダル */}
      <IncompleteWorksModal
        open={showIncompleteWorksModal}
        onClose={() => setShowIncompleteWorksModal(false)}
      />
    </>
  );
};

export default AuthGuard;